import React from 'react';
import { FileDown, BookOpen, Users, Calendar, Building2, Brain, ArrowLeft } from 'lucide-react';

export default function ContactExportHelp() {
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-gray-800 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-light text-white tracking-wide">Contact Export Guide</h1>
              <p className="text-gray-400 text-xs">Complete data export for AI analysis</p>
            </div>
          </div>
          <FileDown className="w-6 h-6 text-blue-400" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Overview */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
            <Brain className="w-6 h-6 text-blue-400" />
            AI-Optimized Contact Export
          </h2>
          <p className="text-gray-300 leading-relaxed mb-4">
            Export comprehensive contact profiles as formatted PDFs or HTML files, specifically organized for AI analysis. 
            Each export includes all available data points, relationships, events, and business information in a structured format 
            that makes it easy to ask AI assistants detailed questions about your contacts.
          </p>
          <div className="bg-blue-900/20 border border-blue-800/60 rounded-md p-4">
            <p className="text-blue-200 text-sm">
              <strong>💡 Pro Tip:</strong> Use these exports with ChatGPT, Claude, or other AI assistants to get insights about 
              relationship opportunities, investment potential, networking strategies, and personalized outreach approaches.
            </p>
          </div>
        </div>

        {/* How to Export */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">🚀 How to Export Contact Data</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-medium">Navigate to a Contact</h3>
                <p className="text-gray-400 text-sm">Go to any vault and select a contact to view their detailed profile.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-medium">Click the Export Button</h3>
                <p className="text-gray-400 text-sm">Look for the "Export PDF" or "Export HTML" button in the contact's action menu or header.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-medium">Choose Your Format</h3>
                <p className="text-gray-400 text-sm">Select PDF for printing or HTML for web viewing. Both formats are AI-optimized.</p>
              </div>
            </div>
          </div>
        </div>

        {/* What's Included */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">📋 What's Included in Each Export</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-400">
                <Users className="w-5 h-5" />
                <h3 className="font-medium">Basic & Personal Info</h3>
              </div>
              <ul className="text-gray-300 text-sm space-y-1 ml-7">
                <li>• Full contact details (names, titles, companies)</li>
                <li>• Multiple email addresses and phone numbers</li>
                <li>• Personal information (family, interests, education)</li>
                <li>• Social media and web presence</li>
                <li>• Physical addresses (primary and secondary)</li>
              </ul>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-blue-400">
                <Building2 className="w-5 h-5" />
                <h3 className="font-medium">Business Intelligence</h3>
              </div>
              <ul className="text-gray-300 text-sm space-y-1 ml-7">
                <li>• Company associations and roles</li>
                <li>• Business contact information</li>
                <li>• Industry and department details</li>
                <li>• Revenue estimates and business size</li>
                <li>• Opportunity pipeline information</li>
              </ul>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-yellow-400">
                <Calendar className="w-5 h-5" />
                <h3 className="font-medium">Events & Interactions</h3>
              </div>
              <ul className="text-gray-300 text-sm space-y-1 ml-7">
                <li>• Complete event history with dates and locations</li>
                <li>• Meeting notes and interaction summaries</li>
                <li>• Participant information</li>
                <li>• Associated tasks and follow-ups</li>
                <li>• Chronological timeline of all activities</li>
              </ul>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-purple-400">
                <BookOpen className="w-5 h-5" />
                <h3 className="font-medium">Relationships & Financial</h3>
              </div>
              <ul className="text-gray-300 text-sm space-y-1 ml-7">
                <li>• Network connections and mutual contacts</li>
                <li>• Financial capacity and investment interests</li>
                <li>• Net worth estimates and accreditation status</li>
                <li>• Investment scores and potential ratings</li>
                <li>• All notes, summaries, and AI analysis</li>
              </ul>
            </div>
          </div>
        </div>

        {/* AI Analysis Guide */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-400" />
            AI Analysis Examples
          </h2>
          <p className="text-gray-300 mb-4">Here are some example prompts you can use with AI assistants after uploading your contact exports:</p>
          
          <div className="space-y-4">
            <div className="bg-black/40 border border-gray-700 rounded-md p-4">
              <h3 className="text-green-400 font-medium mb-2">🎯 Relationship Strategy</h3>
              <p className="text-gray-300 text-sm italic">
                "Based on this contact profile, what's the best approach to strengthen our relationship? 
                Consider their communication history, interests, and mutual connections."
              </p>
            </div>
            
            <div className="bg-black/40 border border-gray-700 rounded-md p-4">
              <h3 className="text-blue-400 font-medium mb-2">💰 Investment Potential</h3>
              <p className="text-gray-300 text-sm italic">
                "Analyze this person's investment profile. What's their likely investment capacity, 
                risk tolerance, and what investment opportunities might appeal to them?"
              </p>
            </div>
            
            <div className="bg-black/40 border border-gray-700 rounded-md p-4">
              <h3 className="text-purple-400 font-medium mb-2">🤝 Referral Opportunities</h3>
              <p className="text-gray-300 text-sm italic">
                "Who in their network might be good referral candidates? Based on their relationships 
                and business connections, suggest 3-5 potential introductions I should ask for."
              </p>
            </div>
            
            <div className="bg-black/40 border border-gray-700 rounded-md p-4">
              <h3 className="text-yellow-400 font-medium mb-2">📧 Personalized Outreach</h3>
              <p className="text-gray-300 text-sm italic">
                "Draft a personalized email to reconnect with this contact. Reference our shared history, 
                their interests, and suggest a specific next step that would provide value to them."
              </p>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">✅ Best Practices</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-green-400 font-medium mb-2">Do's</h3>
              <ul className="text-gray-300 text-sm space-y-1">
                <li>✓ Export regularly updated contact profiles</li>
                <li>✓ Use specific, targeted questions with AI</li>
                <li>✓ Combine multiple contact exports for network analysis</li>
                <li>✓ Review AI suggestions before taking action</li>
                <li>✓ Keep exports organized by date or purpose</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-red-400 font-medium mb-2">Don'ts</h3>
              <ul className="text-gray-300 text-sm space-y-1">
                <li>✗ Share sensitive financial data inappropriately</li>
                <li>✗ Make decisions based solely on AI analysis</li>
                <li>✗ Export contacts without proper permissions</li>
                <li>✗ Store exports in unsecured locations</li>
                <li>✗ Forget to update contact data before exporting</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-8 border-t border-gray-800">
          <p className="text-gray-400 text-sm">
            Contact exports are generated in real-time from your vault data. 
            <br />
            For technical support or feature requests, contact your vault administrator.
          </p>
        </div>
      </div>
    </div>
  );
}